import { ACCOUNT_ROLE_COPY } from "@common/constants/domains/accounting/accountRole";
import { AccountRole } from "@common/types/domains/accounting/account";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { DraftGroup } from "../draftState";
import {
    NestedAddAccountModal,
    NestedAddAccountModalProps,
} from "./NestedAddAccountModal";
import {
    NestedAddGroupModal,
    NestedAddGroupModalProps,
} from "./NestedAddGroupModal";

interface Props {
    // Draft groups feeding the footer placement modals' Group/Parent pickers.
    groups: DraftGroup[];
    // Footer (command-island) ⌥A/⌥G placement modals.
    addAccountModalProps: Omit<NestedAddAccountModalProps, "groups">;
    addGroupModalProps: Omit<NestedAddGroupModalProps, "groups">;
    isDeleteOpen: boolean;
    deleteTitle: string;
    deleteQuestion: string;
    onDeleteConfirm: () => void;
    onDeleteClose: () => void;
    // Non-null while a rename of a role-holding account waits on confirmation.
    roleRenameRole: AccountRole | null;
    onRoleRenameConfirm: () => void;
    onRoleRenameClose: () => void;
}

// The nested editor's modals: the footer placement modals (Add account / Add
// group with a Group/Parent picker) and the delete-confirmation modal (single
// account/group or a bulk set). Per-row "Add group" creates a group inline (no
// modal), mirroring per-row "Add account".
export const NestedEditorModals = ({
    groups,
    addAccountModalProps,
    addGroupModalProps,
    isDeleteOpen,
    deleteTitle,
    deleteQuestion,
    onDeleteConfirm,
    onDeleteClose,
    roleRenameRole,
    onRoleRenameConfirm,
    onRoleRenameClose,
}: Props) => (
    <>
        {/* Always mounted (like ConfirmationModal below) and gated on their own
            `show={isOpen}` so the modal's enter/exit animation runs — a
            conditional mount would unmount before `show` can transition to
            false, skipping the exit transition. */}
        <NestedAddAccountModal groups={groups} {...addAccountModalProps} />
        <NestedAddGroupModal groups={groups} {...addGroupModalProps} />
        <ConfirmationModal
            show={isDeleteOpen}
            title={deleteTitle}
            question={deleteQuestion}
            yes="Delete"
            yesBtnVariant="destructive"
            no="Cancel"
            onConfirm={onDeleteConfirm}
            onReject={onDeleteClose}
            onHide={onDeleteClose}
        />
        <ConfirmationModal
            show={roleRenameRole !== null}
            title={
                roleRenameRole === null
                    ? ""
                    : `Rename the ${ACCOUNT_ROLE_COPY[roleRenameRole].label}?`
            }
            question={
                roleRenameRole === null
                    ? ""
                    : ACCOUNT_ROLE_COPY[roleRenameRole].description
            }
            yes="Rename"
            no="Cancel"
            onConfirm={onRoleRenameConfirm}
            onReject={onRoleRenameClose}
            onHide={onRoleRenameClose}
        />
    </>
);
